import React, { useState, useEffect } from 'react';
import { workoutsAPI, exercisesAPI } from '../../api';
import { Plus, Search, Dumbbell, Clock, BarChart3, Edit, Trash2, X, ChevronDown, Users } from 'lucide-react';

const DIFFICULTY_OPTIONS = ['beginner', 'intermediate', 'advanced'];
const CATEGORY_OPTIONS = ['Push', 'Pull', 'Legs', 'Upper Body', 'Lower Body', 'Full Body', 'Core', 'Cardio', 'Custom'];

export function WorkoutTemplates({ userRole }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // master_admin treated as admin-equivalent for create/edit on the
  // template gallery: they're a super-admin of the platform and should
  // be able to create templates for any org, just like the org admin
  // (whose `require_trainer` already accepts master_admin on the BE).
  const canEdit = userRole === 'admin' || userRole === 'master_admin' || userRole === 'trainer';

  useEffect(() => {
    fetchTemplates();
  }, [filterCategory, filterDifficulty]);

  const fetchTemplates = async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterDifficulty) params.difficulty = filterDifficulty;
      
      const response = await workoutsAPI.listTemplates(params);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await workoutsAPI.deleteTemplate(templateId);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="workout-templates">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workout Templates</h1>
          <p className="text-gray-600">Create and manage workout routines</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditingTemplate(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            data-testid="create-template-btn"
          >
            <Plus className="w-5 h-5" />
            Create Template
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-4">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
            data-testid="template-category-filter"
          >
            <option value="">All Categories</option>
            {CATEGORY_OPTIONS.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
            data-testid="template-difficulty-filter"
          >
            <option value="">All Difficulties</option>
            {DIFFICULTY_OPTIONS.map(diff => (
              <option key={diff} value={diff}>{diff.charAt(0).toUpperCase() + diff.slice(1)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Dumbbell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No templates found</h3>
          <p className="text-gray-500 mt-1">Create your first workout template</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {templates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              canEdit={canEdit}
              onEdit={() => { setEditingTemplate(template); setShowForm(true); }}
              onDelete={() => handleDelete(template.id)}
            />
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <TemplateForm
          template={editingTemplate}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); fetchTemplates(); }}
        />
      )}
    </div>
  );
}

function TemplateCard({ template, canEdit, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  const difficultyColors = {
    beginner: 'bg-green-100 text-green-700',
    intermediate: 'bg-yellow-100 text-yellow-700',
    advanced: 'bg-red-100 text-red-700'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition" data-testid={`template-card-${template.id}`}>
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{template.name}</h3>
            <p className="text-sm text-gray-500 mt-1">by {template.trainer_name}</p>
          </div>
          {canEdit && (
            <div className="flex gap-1">
              <button onClick={onEdit} className="p-1 text-gray-400 hover:text-indigo-600">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={onDelete} className="p-1 text-gray-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {template.category && (
            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
              {template.category}
            </span>
          )}
          <span className={`px-2 py-0.5 text-xs rounded-full ${difficultyColors[template.difficulty] || 'bg-gray-100 text-gray-700'}`}>
            {template.difficulty}
          </span>
        </div>

        {template.description && (
          <p className="text-sm text-gray-600 mt-3 line-clamp-2">{template.description}</p>
        )}

        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Dumbbell className="w-4 h-4" />
            {template.exercises.length} exercises
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {template.estimated_duration_minutes} min
          </div>
          {template.times_used > 0 && (
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {template.times_used}x used
            </div>
          )}
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-sm text-indigo-600 mt-3 hover:text-indigo-700"
        >
          <ChevronDown className={`w-4 h-4 transition ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? 'Hide' : 'Show'} Exercises
        </button>

        {expanded && (
          <div className="mt-3 space-y-2">
            {template.exercises.map((ex, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg text-sm">
                <span className="text-gray-400 font-mono w-5">{idx + 1}</span>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{ex.exercise_name || 'Unknown'}</p>
                  <p className="text-gray-500">
                    {ex.sets} sets × {ex.reps} {ex.weight && `@ ${ex.weight}`}
                  </p>
                </div>
                <span className="text-xs text-gray-400">{ex.rest_seconds}s rest</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateForm({ template, onClose, onSave }) {
  const [exercises, setExercises] = useState([]);
  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    category: template?.category || '',
    estimated_duration_minutes: template?.estimated_duration_minutes || 60,
    difficulty: template?.difficulty || 'intermediate',
    exercises: template?.exercises || []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    exercisesAPI.list().then(res => setExercises(res.data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }
    if (formData.exercises.length === 0) {
      setError('Add at least one exercise');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Ensure proper order
      const orderedExercises = formData.exercises.map((ex, idx) => ({
        ...ex,
        order: idx
      }));

      if (template) {
        await workoutsAPI.updateTemplate(template.id, { ...formData, exercises: orderedExercises });
      } else {
        await workoutsAPI.createTemplate({ ...formData, exercises: orderedExercises });
      }
      onSave();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const addExercise = () => {
    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, {
        exercise_id: '',
        sets: 3,
        reps: '10',
        weight: '',
        rest_seconds: 60,
        notes: '',
        order: prev.exercises.length
      }]
    }));
  };

  const updateExercise = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.map((ex, i) =>
        i === idx ? { ...ex, [field]: value } : ex
      )
    }));
  };

  const removeExercise = (idx) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== idx)
    }));
  };

  const moveExercise = (idx, direction) => {
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= formData.exercises.length) return;

    setFormData(prev => {
      const newExercises = [...prev.exercises];
      [newExercises[idx], newExercises[newIdx]] = [newExercises[newIdx], newExercises[idx]];
      return { ...prev, exercises: newExercises };
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="template-form-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {template ? 'Edit Template' : 'Create Template'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select category...</option>
                  {CATEGORY_OPTIONS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  {DIFFICULTY_OPTIONS.map(diff => (
                    <option key={diff} value={diff}>{diff.charAt(0).toUpperCase() + diff.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
                <input
                  type="number"
                  value={formData.estimated_duration_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration_minutes: parseInt(e.target.value) || 60 }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Exercises */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">Exercises *</label>
                <button
                  type="button"
                  onClick={addExercise}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                  Add Exercise
                </button>
              </div>

              <div className="space-y-3">
                {formData.exercises.map((ex, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-500 w-6">{idx + 1}</span>
                      <select
                        value={ex.exercise_id}
                        onChange={(e) => updateExercise(idx, 'exercise_id', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                      >
                        <option value="">Select exercise...</option>
                        {exercises.map(exercise => (
                          <option key={exercise.id} value={exercise.id}>{exercise.name}</option>
                        ))}
                      </select>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => moveExercise(idx, 'up')}
                          disabled={idx === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveExercise(idx, 'down')}
                          disabled={idx === formData.exercises.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removeExercise(idx)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 ml-6">
                      <div>
                        <label className="text-xs text-gray-500">Sets</label>
                        <input
                          type="number"
                          value={ex.sets}
                          onChange={(e) => updateExercise(idx, 'sets', parseInt(e.target.value) || 3)}
                          className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Reps</label>
                        <input
                          type="text"
                          value={ex.reps}
                          onChange={(e) => updateExercise(idx, 'reps', e.target.value)}
                          placeholder="10 or 8-12"
                          className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Weight</label>
                        <input
                          type="text"
                          value={ex.weight || ''}
                          onChange={(e) => updateExercise(idx, 'weight', e.target.value)}
                          placeholder="50kg"
                          className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Rest (s)</label>
                        <input
                          type="number"
                          value={ex.rest_seconds}
                          onChange={(e) => updateExercise(idx, 'rest_seconds', parseInt(e.target.value) || 60)}
                          className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {formData.exercises.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No exercises added yet</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="save-template-btn"
              >
                {saving ? 'Saving...' : (template ? 'Update' : 'Create')} Template
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default WorkoutTemplates;
